import {
  CollectionAsset,
  CollectionChannelAsset,
  CollectionEpisodeAsset,
  CollectionLinkAsset,
  CollectionMovieAsset,
  CollectionProgramAsset,
  CollectionSeriesAsset,
} from '../../data/collection/types';
import { convertMediaTypeToRhinoAssetType } from '../../data/compat/rhinoAssetTypeToMediaType';
import { tvpil } from '../../data/compat/tvpil';
import type { RhinoLinkDisplayType, RhinoLinkType, RhinoRating } from '../../data/type';
import { KalturaCollectionAsset } from '../composed';
import { fromKalturaBoolean } from '../utils';

import { processPhoenixImages } from './processing';
import { processVodAssetMetas } from './vod';

const processors = {
  episode: processEpisode,
  link: processLink,
  movie: processMovie,
  program: processProgram,
  series: processSeries,
  linear: processChannel,
};

export function processCollectionAsset(asset: KalturaCollectionAsset): CollectionAsset {
  const type = convertMediaTypeToRhinoAssetType(tvpil.dms.mediaTypesValue[asset.type]);

  const process = processors[type as keyof typeof processors] ?? processMovie;

  return process(asset);
}

/**
 * @see https://kaltura.atlassian.net/wiki/spaces/XTVEXT/pages/1593611328/VOD+Metadata+Ingest+Group#Special-Elements-mapping-for-an-Episode-Title-Asset[hardBreak]
 */
function processEpisode(asset: KalturaCollectionAsset): CollectionEpisodeAsset {
  return {
    ...processVodAssetMetas(asset.metas),
    id: asset.id,
    isAdult: fromKalturaBoolean(asset.metas['Adult']),
    title: asset.name,
    images: processPhoenixImages(asset.images),
    rating: getRating(asset),
    type: 'episode',
    ...(asset.tags['BillingId'] && {
      billingPlans: asset.tags['BillingId'].objects.map(({ value }) => value),
    }),
    ...(asset.metas['DeeplinkURN'] && {
      deeplinkUrn: String(asset.metas['DeeplinkURN'].value),
    }),
    ...(asset.metas['Deeplink'] && {
      deeplink: String(asset.metas['Deeplink'].value),
    }),
    ...(asset.metas['EpisodeName'] && {
      episodeName: String(asset.metas['EpisodeName'].value),
    }),
    ...(asset.metas['EpisodeNumber'] && {
      episodeNumber: Number(asset.metas['EpisodeNumber'].value),
    }),
    ...(asset.tags['ParentalRating'] && {
      parentalRating: Number(asset.tags['ParentalRating'].objects[0].value),
    }),
    ...(asset.metas['Is_Deeplink'] && {
      shouldUseDeeplink: Boolean(asset.metas['Is_Deeplink'].value),
    }),
    ...(asset.metas['SeasonNumber'] && {
      seasonNumber: Number(asset.metas['SeasonNumber'].value),
    }),
    ...(asset.metas['SeriesID'] && {
      seriesId: String(asset.metas['SeriesID'].value),
    }),
    ...(asset.tags['SeriesName'] && {
      seriesName: String(asset.tags['SeriesName'].objects[0].value),
    }),
    hasMediaFiles: Boolean(asset.mediaFiles?.length),
    providerName: getProviderNameFromMeta(asset.metas),
  };
}

function processLink(asset: KalturaCollectionAsset): CollectionLinkAsset {
  return {
    id: asset.id,
    images: processPhoenixImages(asset.images),
    isAdult: fromKalturaBoolean(asset.metas['Adult']),
    title: asset.name,
    type: 'link',
    ...(asset.metas['in_app_link'] && { inAppLink: String(asset.metas['in_app_link'].value) }),
    ...(asset.metas['attached_collection_id'] && {
      attachedCollectionId: Number(asset.metas['attached_collection_id'].value),
    }),
    ...(asset.tags['type_of_app_link'] && {
      linkType: asset.tags['type_of_app_link'].objects.map(({ value }) => value)[0] as RhinoLinkType,
    }),
    ...(asset.tags['display_type'] && {
      displayType: (asset.tags['display_type'].objects.map(({ value }) => value)[0] as RhinoLinkDisplayType) ?? 'text',
    }),
  };
}

/**
 * @see https://kaltura.atlassian.net/wiki/spaces/XTVEXT/pages/1593611328/VOD+Metadata+Ingest+Group#[inlineExtension]Elements-mapping-for-a-Title-Asset-(/ADI/Asset/Metadata/AMS[@Asset_Class=%22Title%22]/Metadata/)[hardBreak]
 */
function processMovie(asset: KalturaCollectionAsset): CollectionMovieAsset {
  return {
    ...processVodAssetMetas(asset.metas),
    id: asset.id,
    isAdult: fromKalturaBoolean(asset.metas['Adult']),
    title: asset.name,
    images: processPhoenixImages(asset.images),
    rating: getRating(asset),
    type: 'movie',
    ...(asset.tags['BillingId'] && {
      billingPlans: asset.tags['BillingId'].objects.map(({ value }) => value),
    }),
    ...(asset.metas['DeeplinkURN'] && {
      deeplinkUrn: String(asset.metas['DeeplinkURN'].value),
    }),
    ...(asset.metas['Deeplink'] && {
      deeplink: String(asset.metas['Deeplink'].value),
    }),
    ...(asset.tags['ParentalRating'] && {
      parentalRating: Number(asset.tags['ParentalRating'].objects[0].value),
    }),
    ...(asset.metas['Is_Deeplink'] && {
      shouldUseDeeplink: Boolean(asset.metas['Is_Deeplink'].value),
    }),
    hasMediaFiles: Boolean(asset.mediaFiles?.length),
    providerName: getProviderNameFromMeta(asset.metas),
  };
}

/**
 * @see https://kaltura.atlassian.net/wiki/spaces/XTVEXT/pages/1447559934/Linear+EPG+Ingest#Input-XMLTV-File-Specification
 */
function processProgram(asset: KalturaCollectionAsset): CollectionProgramAsset {
  return {
    channelId: asset.linearAssetId,
    enableCatchUp: asset.enableCatchUp,
    endDate: asset.endDate * 1000,
    id: asset.id,
    isAdult: fromKalturaBoolean(asset.metas['Adult']),
    images: processPhoenixImages(asset.images),
    rating: getRating(asset),
    providerName: getProviderNameFromMeta(asset.metas),
    startDate: asset.startDate * 1000,
    title: asset.name,
    type: 'program',
    ...(asset.metas['EpisodeName'] && {
      episodeName: String(asset.metas['EpisodeName'].value),
    }),
    ...(asset.metas['EpisodeNumber'] && {
      episodeNumber: Number(asset.metas['EpisodeNumber'].value),
    }),
    ...(asset.tags['ParentalRating'] && {
      parentalRating: Number(asset.tags['ParentalRating'].objects[0].value),
    }),
    ...(asset.metas['SeasonNumber'] && {
      seasonNumber: Number(asset.metas['SeasonNumber'].value),
    }),
    ...(asset.metas['SeriesID'] && {
      seriesId: String(asset.metas['SeriesID'].value),
    }),
    ...(asset.tags['SeriesName'] && {
      seriesName: String(asset.tags['SeriesName'].objects[0].value),
    }),
  };
}

/**
 * @see https://kaltura.atlassian.net/wiki/spaces/XTVEXT/pages/1593611328/VOD+Metadata+Ingest+Group#Special-Elements-mapping-for-a-Series-Title-Asset[hardBreak]
 */
function processSeries(asset: KalturaCollectionAsset): CollectionSeriesAsset {
  return {
    ...processVodAssetMetas(asset.metas),
    id: asset.id,
    isAdult: fromKalturaBoolean(asset.metas['Adult']),
    title: asset.name,
    images: processPhoenixImages(asset.images),
    rating: getRating(asset),
    type: 'series',
    ...(asset.tags['ParentalRating'] && {
      parentalRating: Number(asset.tags['ParentalRating'].objects[0].value),
    }),
    ...(asset.metas['Is_Deeplink'] && {
      shouldUseDeeplink: Boolean(asset.metas['Is_Deeplink'].value),
    }),
    ...(asset.metas['SeriesID'] && {
      seriesId: String(asset.metas['SeriesID'].value),
    }),
    hasMediaFiles: Boolean(asset.mediaFiles?.length),
    providerName: getProviderNameFromMeta(asset.metas),
  };
}

function processChannel(asset: KalturaCollectionAsset): CollectionChannelAsset {
  return {
    id: asset.id,
    images: processPhoenixImages(asset.images),
    isAdult: fromKalturaBoolean(asset.metas['Adult']),
    ...(asset.tags['ParentalRating'] && {
      parentalRating: Number(asset.tags['ParentalRating'].objects[0].value),
    }),
    title: asset.name,
    type: 'linear',
  };
}

/**
 * @see https://kaltura.atlassian.net/wiki/spaces/KUXDEV/pages/3613229074/Rating+Spec#TVPIL-Task
 */
function getRating(asset: KalturaCollectionAsset): RhinoRating | undefined {
  const rating = tvpil.availableRatings.find((r) => {
    const metaValue = asset.metas[r.name]?.value;

    return metaValue && parseInt(metaValue as string) > -1;
  });

  if (!rating) {
    return undefined;
  }

  return {
    name: rating.name,
    value: asset.metas[rating.name]?.value as string,
  };
}

function getProviderNameFromMeta(metas: KalturaCollectionAsset['metas']) {
  const providerNames = Object.keys(tvpil.providers);

  for (let i = 0; i < providerNames.length; i++) {
    const provider = tvpil.providers[providerNames[i]];

    const assetProvider = (metas[provider.metaKey]?.value as unknown as string)?.toLowerCase();

    if (assetProvider === provider.metaValue?.toLowerCase()) {
      return providerNames[i];
    }
  }
}
